import { option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { keyBy } from 'lodash/fp';
import { FeatureFlag } from '@mews-distributor/common';
import { values } from '@mews/fp';
import { DateTimeUtcService, DateTimeUtils, TimeService, WallClockDateService } from '@mews/framework';
import { setEndDate, setEndTimeUtc, setStartDate, setStartTimeUtc } from '../../actions/dates';
import { getEnterprises, getEnterpriseTimezone } from '../../modules/entities/enterprises/selectors';
import { getEnterpriseServiceId, isServiceHourly } from '../../modules/entities/services/selectors';
import { getInitialOccupancyData } from '../../modules/occupancy/selectors';
import {
    getEndTime,
    getParsedEndDate,
    getParsedStartDate,
    getSelectedHotelId,
    getStartTime,
} from '../../modules/resourceOrder/selectors';
import { getFirstAvailableDateTime, getFirstAvailableStartTime } from '../../modules/serviceAvailability/hourlyUtils';
import { getServiceAvailabilityData } from '../../modules/serviceAvailability/selectors';
import { getSortedEnterpriseAgeCategories } from '../../selectors/views/ageCategories/selectors';
import { lastFetchedDateInServiceAvailability } from '../../selectors/views/dates';
import { DistributorThunkAction } from '../../types';
import { midnight } from './components/DateRange/utils';
import { isTimeSelected } from './slices/calendar';
import { enterpriseDateOccupanciesSet } from './slices/occupancy';

export const initializeDatesOccupancies = (): DistributorThunkAction => (dispatch, getState) => {
    const state = getState();

    const enterprises = pipe(getEnterprises(state), values);

    enterprises.forEach(({ id: enterpriseId }) => {
        if (enterpriseId == null) {
            return;
        }

        const serviceId = getEnterpriseServiceId(getState(), { enterpriseId });
        if (serviceId == null) {
            return;
        }

        const applicationInitialOccupancies = getInitialOccupancyData(getState(), { serviceId });
        const ageCategoriesIds = getSortedEnterpriseAgeCategories(getState(), { enterpriseId });
        const initialDateOccupancies = ageCategoriesIds.map((categoryId) => {
            if (applicationInitialOccupancies.some(({ ageCategoryId }) => ageCategoryId === categoryId)) {
                return (
                    applicationInitialOccupancies.find(
                        (appInitialOccupancy) => appInitialOccupancy.ageCategoryId === categoryId
                    ) ?? { ageCategoryId: categoryId, personCount: 0 }
                );
            }

            return { ageCategoryId: categoryId, personCount: 0 };
        });
        dispatch(
            enterpriseDateOccupanciesSet({ enterpriseId, occupancy: keyBy('ageCategoryId', initialDateOccupancies) })
        );
    });
};

const MIN_DEFAULT_HOURS = 2;

export const getValidStartTime =
    (): DistributorThunkAction =>
    (dispatch, getState, { featureFlagService, appClockService, calendarService }) => {
        const state = getState();
        const enterpriseId = getSelectedHotelId(state);
        const startDate = getParsedStartDate(state);
        const endDate = getParsedEndDate(state);
        const timezone = getEnterpriseTimezone(state, { enterpriseId });
        const isOvernightBookingEnabled = featureFlagService.hasFlag(FeatureFlag.overMidnightHourlyBookingsEnabled);
        const isBookingEngineForHourlyServiceEnabled = featureFlagService.hasFlag(
            FeatureFlag.bookingEngineForHourlyServiceEnabled
        );
        const isHourlyService = isServiceHourly(state, { enterpriseId });
        const isHourlyServiceEnabled = isBookingEngineForHourlyServiceEnabled && isHourlyService;
        if (!isHourlyServiceEnabled || timezone == null || startDate == null || endDate == null) {
            return;
        }

        const isTimeUserSelected = isTimeSelected(state);
        const startTime = getStartTime(state, { enterpriseId });
        const endTime = getEndTime(state, { enterpriseId });

        const hasStartDateTime = startDate != null && startTime != null;
        const hasEndDateTime = endDate != null && endTime != null;
        // Create date from the date picker and the time picker.
        const startTimeUtc = hasStartDateTime
            ? DateTimeUtils.createDateTimeUtc({
                  date: startDate,
                  time: startTime,
                  timezone,
              })
            : null;

        const overMidnightAdjustedEndDate =
            startTime != null && hasEndDateTime && TimeService.isLessOrEqual(endTime, startTime)
                ? WallClockDateService.addDays(startDate, 1)
                : startDate;

        const adjustedEndDate =
            hasEndDateTime && TimeService.isEqual(endTime, midnight)
                ? WallClockDateService.addDays(startDate, 1)
                : startDate;

        const endTimeUtc = hasEndDateTime
            ? DateTimeUtils.createDateTimeUtc({
                  date: isOvernightBookingEnabled ? overMidnightAdjustedEndDate : adjustedEndDate,
                  time: endTime,
                  timezone,
              })
            : null;

        // Set valid times if they both exist.
        if (startTimeUtc != null && endTimeUtc != null) {
            dispatch(setStartTimeUtc(startTimeUtc, timezone, isOvernightBookingEnabled));
            dispatch(setEndTimeUtc(endTimeUtc, timezone, isOvernightBookingEnabled));
            return;
        }

        // Set a default end time if start time was set but not end time.
        if (startTimeUtc != null && isTimeUserSelected && endTime == null) {
            dispatch(setEndTimeUtc(DateTimeUtcService.addHours(startTimeUtc, 1), timezone, isOvernightBookingEnabled));
        }

        const data = getServiceAvailabilityData(state);

        const nowUtc = appClockService.getAppNowUtc();
        const todayDate = DateTimeUtils.utcToStructuredDate(nowUtc.value, timezone);
        const isDateToday = calendarService.isDateEqual(
            DateTimeUtils.wallClockDateToStructuredDate(startDate, calendarService),
            todayDate
        );

        const userTimeInHours = new Date().getHours();

        const startDateTime = isDateToday
            ? // Default start time should be the beginning of the next full hour. ie. if it is 08:01, the time should be 10
              DateTimeUtils.createDateTimeUtc({
                  date: startDate,
                  time: TimeService.addHours(midnight, userTimeInHours + MIN_DEFAULT_HOURS),
                  timezone,
              })
            : DateTimeUtils.createDateTimeUtc({
                  date: startDate,
                  time: midnight,
                  timezone,
              });

        const endOfToday = pipe(DateTimeUtcService.endOf(nowUtc, 'day'));
        const hoursUntilMidnight = DateTimeUtcService.diffHours(endOfToday, nowUtc);
        const shouldAdvanceToNextDay = isDateToday && hoursUntilMidnight < MIN_DEFAULT_HOURS;

        let startDateUpdated = startDate;
        let startUtc = shouldAdvanceToNextDay
            ? DateTimeUtils.createDateTimeUtc({
                  date: WallClockDateService.addDays(startDate, 1),
                  time: midnight,
                  timezone,
              })
            : startDateTime;

        let firstAvailabileStartTime = isOvernightBookingEnabled
            ? getFirstAvailableDateTime({ startUtc, data, timezone })
            : getFirstAvailableStartTime({ startUtc, data, timezone });

        const lastDateInData = pipe(
            lastFetchedDateInServiceAvailability(state, timezone),
            option.fromNullable,
            option.match(
                () => startUtc,
                (date) => DateTimeUtils.createDateTimeUtc({ date, time: midnight, timezone })
            )
        );

        // Advance the date until we find a valid start time.
        while (firstAvailabileStartTime == null && DateTimeUtcService.isLess(startUtc, lastDateInData)) {
            startDateUpdated = WallClockDateService.addDays(startDateUpdated, 1);
            startUtc = DateTimeUtils.createDateTimeUtc({
                date: startDateUpdated,
                time: midnight,
                timezone,
            });
            firstAvailabileStartTime = isOvernightBookingEnabled
                ? getFirstAvailableDateTime({ startUtc, data, timezone })
                : getFirstAvailableStartTime({ startUtc, data, timezone });
        }

        // Set a default time if there is none.
        if (!isTimeUserSelected && firstAvailabileStartTime != null) {
            const nextAvailableStartUtc = firstAvailabileStartTime ?? startUtc;
            const endUtc = DateTimeUtcService.addHours(nextAvailableStartUtc, 1);

            const newStartDate = DateTimeUtils.dateTimeUtcStringToWallClockDate(nextAvailableStartUtc.value, timezone);
            if (WallClockDateService.isGreater(newStartDate, startDate)) {
                dispatch(setStartDate(newStartDate.value));
                dispatch(setEndDate(newStartDate.value));
            }

            dispatch(setStartTimeUtc(nextAvailableStartUtc, timezone, isOvernightBookingEnabled));
            dispatch(setEndTimeUtc(endUtc, timezone, isOvernightBookingEnabled));
            return;
        }

        /**
         * If no valid start time is found, set the default time to closest time.
         * The category page will show a message that there are no available times.
         */
        const hour = DateTimeUtcService.startOf(startTimeUtc ?? nowUtc, 'hour');
        dispatch(setStartTimeUtc(hour, timezone, isOvernightBookingEnabled));
        dispatch(setEndTimeUtc(DateTimeUtcService.addHours(hour, 1), timezone, isOvernightBookingEnabled));
    };
